import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { setupApplicationSetup } from "../util/AppUtils";

const baseUrl = BASE_URL + "app-setup";

const getToken = () => {
  let token = "";
  if (localStorage.rememberMe === "true") {
    token = localStorage.token;
  } else {
    token = sessionStorage.token;
  }
  return token || "";
};

const toFormData = (data) => {
  const formData = new FormData();
  if (data.signature_auth_methods) {
    const methods = Array.isArray(data.signature_auth_methods)
      ? data.signature_auth_methods
      : data.signature_auth_methods?.methods || [];
    formData.append("signature_auth_methods", JSON.stringify(methods));
  }
  if (data.company_name) formData.append("company_name", data.company_name);
  if (data.company_address)
    formData.append("company_address", data.company_address);
  if (data.company_phone) formData.append("company_phone", data.company_phone);
  if (data.footer) formData.append("footer", data.footer);
  // Prefer URL over base64 - send URL in company_image field as string
  if (data.company_image_url) {
    formData.append("company_image", data.company_image_url);
  } else if (data.company_image_base64) {
    formData.append("company_image", data.company_image_base64);
  }
  if (data.audit_trail_data) {
    formData.append("audit_trail_data", JSON.stringify(data.audit_trail_data));
  }
  if (data.password) {
    formData.append("password", data.password);
  }
  // Add auto backup fields
  if (data.is_autobackup_enabled !== undefined) {
    formData.append("is_autobackup_enabled", data.is_autobackup_enabled.toString());
  }
  if (data.auto_backup_time) {
    formData.append("auto_backup_time", data.auto_backup_time);
  }
  if (data.application_time_zone) {
    formData.append("application_time_zone", data.application_time_zone);
  }
  if (data.backup_retention_policy !== undefined) {
    formData.append("backup_retention_policy", JSON.stringify(data.backup_retention_policy));
  }
  return formData;
};

// Create
export const createAppSetup = createAsyncThunk(
  "appSetup/create",
  async (payload, { rejectWithValue }) => {
    try {
      const formData = toFormData(payload);
      const response = await axios.post(`${baseUrl}/create`, formData, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      });
      // Check if response indicates failure (success: false)
      if (response.data && response.data.success === false) {
        return rejectWithValue(response.data.message || response.data.error || "Operation failed");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

// Update
export const updateAppSetup = createAsyncThunk(
  "appSetup/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const formData = toFormData(data);
      const response = await axios.put(`${baseUrl}/update/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      });
      // Check if response indicates failure (success: false)
      if (response.data && response.data.success === false) {
        return rejectWithValue(response.data.message || response.data.error || "Operation failed");
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

// Get by id
export const getAppSetupById = createAsyncThunk(
  "appSetup/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

// Get all
export const getAllAppSetups = createAsyncThunk(
  "appSetup/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-all`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setupApplicationSetup(response.data.results)
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

// Get app setup data (public endpoint for login page)
export const getAppSetupData = createAsyncThunk(
  "appSetup/getData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-data`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

// Delete
export const deleteAppSetup = createAsyncThunk(
  "appSetup/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${baseUrl}/delete/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

const appSetupSlice = createSlice({
  name: "appSetup",
  initialState: {
    loading: false,
    success: false,
    error: null,
    items: [],
    current: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // create
      .addCase(createAppSetup.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createAppSetup.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.current = action.payload?.data || action.payload || null;
      })
      .addCase(createAppSetup.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      // update
      .addCase(updateAppSetup.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(updateAppSetup.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.current = action.payload?.data || action.payload || null;
      })
      .addCase(updateAppSetup.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      // getById
      .addCase(getAppSetupById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAppSetupById.fulfilled, (state, action) => {
        state.loading = false;
        state.current =
          action.payload?.data ||
          action.payload?.result ||
          action.payload ||
          null;
      })
      .addCase(getAppSetupById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // getAll
      .addCase(getAllAppSetups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllAppSetups.fulfilled, (state, action) => {
        state.loading = false;
        const list =
          action.payload?.data ||
          action.payload?.results ||
          action.payload ||
          [];
        state.items = Array.isArray(list) ? list : [];
      })
      .addCase(getAllAppSetups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // delete
      .addCase(deleteAppSetup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAppSetup.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // best-effort remove if id returned
        const deletedId = action.meta.arg;
        state.items = state.items.filter(
          (it) =>
            it.id !== deletedId && it._id !== deletedId && it.uuid !== deletedId
        );
        if (
          state.current &&
          (state.current.id === deletedId ||
            state.current._id === deletedId ||
            state.current.uuid === deletedId)
        ) {
          state.current = null;
        }
      })
      .addCase(deleteAppSetup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // getData
      .addCase(getAppSetupData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAppSetupData.fulfilled, (state, action) => {
        state.loading = false;
        state.current =
          action.payload?.data ||
          action.payload?.result ||
          action.payload ||
          null;
      })
      .addCase(getAppSetupData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default appSetupSlice.reducer;
